import React, { useEffect, useState, useCallback } from "react";
import { useDragScroll } from "../hooks/useDragScroll";
import { getTrackGradient } from "../utils/trackHelpers";

interface TrackSelectorProps {
  trackEntries: [string, any][];
  currentTrackIndex: number;
  onTrackChange: (index: number) => void;
  /** Rendered on the right of the track pills row (e.g. mobile minimise control). */
  trailingContent?: React.ReactNode;
}

// Shortened display names for bubbles
const BUBBLE_DISPLAY_NAMES: Record<string, string> = {
  MAIN: "Adult",
  BK: "BattleKart",
  MINI: "Mini",
  JUNIOR: "Junior",
  INTERMEDIATE: "Intermediate",
  MEMBERSHIP: "Member",
  ARCADE: "Arcades",
};

export const TrackSelector: React.FC<TrackSelectorProps> = React.memo(({ trackEntries, currentTrackIndex, onTrackChange, trailingContent }) => {
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Use custom drag scroll hook
  const { scrollContainerRef, isDragging, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave } = useDragScroll();

  // Check scroll position to show/hide fade indicators
  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, [scrollContainerRef]);

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScroll);
      }
      window.removeEventListener("resize", checkScroll);
    };
  }, [trackEntries, scrollContainerRef, checkScroll]);

  // Scroll selected item into view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const selectedButton = container.querySelector(`[data-index="${currentTrackIndex}"]`);
      if (selectedButton) {
        selectedButton.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }
  }, [currentTrackIndex, scrollContainerRef]);

  // Hide when empty, or when there's only one track — the Adult/Kids tag is redundant then.
  if (trackEntries.length <= 1) return null;

  return (
    <div className="tw-relative tw-w-full tw-px-[8px] tw-pt-[6px]">
      <div className="tw-flex tw-items-center tw-gap-[6px]">
        <div className="tw-relative tw-min-w-0 tw-flex-1">
          {/* Left fade indicator */}
          {showLeftFade && (
            <div className="tw-pointer-events-none tw-absolute tw-bottom-[8px] tw-left-0 tw-top-0 tw-z-10 tw-w-[28px] tw-bg-gradient-to-r tw-from-[#393B41] tw-to-transparent" />
          )}

          {/* Scrollable container */}
          <div
            ref={scrollContainerRef}
            className={`tw-flex tw-gap-[6px] tw-overflow-x-auto tw-scrollbar-none tw-pb-[2px] ${isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab"}`}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {trackEntries.map(([trackKey, trackData], index) => {
              const isSelected = index === currentTrackIndex;
              const displayName = BUBBLE_DISPLAY_NAMES[trackKey] || trackData.trackType;
              const gradient = getTrackGradient(trackKey);

              return (
                <div
                  key={trackKey}
                  data-index={index}
                  onClick={() => onTrackChange(index)}
                  className="tw-flex-shrink-0 tw-rounded-full tw-p-[2px] tw-cursor-pointer tw-transition-all tw-duration-200"
                  style={{ background: isSelected ? gradient : "#000000" }}
                >
                  <div
                    className={`tw-px-[12px] tw-py-[3px] tw-rounded-full tw-text-[12px] tw-font-semibold tw-text-white tw-bg-black ${isSelected ? "" : "hover:tw-opacity-80"}`}
                  >
                    {displayName}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right fade indicator */}
          {showRightFade && (
            <div className="tw-pointer-events-none tw-absolute tw-bottom-[8px] tw-right-0 tw-top-0 tw-z-10 tw-w-[28px] tw-bg-gradient-to-l tw-from-[#393B41] tw-to-transparent" />
          )}
        </div>

        {trailingContent}
      </div>
    </div>
  );
});

TrackSelector.displayName = "TrackSelector";
